import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * <jane-buybox> — drives the Jane product buy box:
 * tier radios update the live total, an optional upsell toggle adds a second
 * line, and Add-to-cart posts items[] to the cart and refreshes the drawer.
 * Progressive: without JS the form still posts the selected tier variant id.
 */
class JaneBuybox extends HTMLElement {
  connectedCallback() {
    this.addUrl = this.dataset.cartAdd || (window.Theme && Theme.routes.cart_add_url);
    this.form = this.querySelector('[data-buybox-form]');
    this.buyBtn = this.querySelector('[ref="buyButton"]');
    this.buyTotal = this.querySelector('[ref="buyTotal"]');
    this.stickyPrice = this.querySelector('[ref="stickyPrice"]');
    this.stickyBtn = this.querySelector('[ref="stickyButton"]');
    this.sticky = this.querySelector('[ref="sticky"]');
    this.upsell = this.querySelector('[ref="upsellToggle"]');
    this.actions = this.querySelector('.jane-buybox__actions');
    this.thumbs = this.querySelectorAll('.jane-buybox__thumb');
    this.mainImage = this.querySelector('[ref="mainImage"]');

    if (this.form) {
      this.form.addEventListener('change', () => {
        this.updateTotal();
        this.syncVariantImage();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart();
      });
    }
    if (this.stickyBtn) this.stickyBtn.addEventListener('click', () => this.addToCart());

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.swapImage(thumb));
    });

    this.updateTotal();
    this.syncVariantImage();
    this.observeSticky();
  }

  selectedOption() {
    const checked = this.form && this.form.querySelector('.jane-buybox__radio:checked');
    return checked ? checked.closest('.jane-buybox__opt') : null;
  }

  isUpsellOn() {
    return Boolean(this.upsell && this.upsell.checked);
  }

  updateTotal() {
    const option = this.selectedOption();
    if (!option) return;
    const total = this.isUpsellOn() && option.dataset.totalUpsell ? option.dataset.totalUpsell : option.dataset.total;
    if (this.buyTotal) this.buyTotal.textContent = total || '';
    if (this.stickyPrice) this.stickyPrice.textContent = total || '';
  }

  syncVariantImage() {
    const option = this.selectedOption();
    if (!option || !this.mainImage) return;
    const image = option.dataset.variantImage;
    if (image) {
      this.mainImage.src = image;
      this.mainImage.removeAttribute('srcset');
    }
  }

  swapImage(thumb) {
    const full = thumb.dataset.full;
    if (this.mainImage && full) {
      this.mainImage.src = full;
      this.mainImage.removeAttribute('srcset');
    }
    this.thumbs.forEach((other) => {
      const active = other === thumb;
      other.classList.toggle('is-active', active);
      if (active) other.setAttribute('aria-current', 'true');
      else other.removeAttribute('aria-current');
    });
  }

  async addToCart() {
    const option = this.selectedOption();
    if (!option || !this.addUrl) return;

    const items = [{ id: Number(option.dataset.variantId), quantity: 1 }];
    if (this.isUpsellOn()) {
      const upsellEl = this.querySelector('[data-upsell-variant]');
      if (upsellEl) items.push({ id: Number(upsellEl.dataset.upsellVariant), quantity: 1 });
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      sectionIds.push(component.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (data.status) {
        deferred.reject(new Error(data.message || 'Add to cart failed'));
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'jane-buybox', didError: false },
      });
      this.flashAdded();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
    }
  }

  flashAdded() {
    [this.buyBtn, this.stickyBtn].forEach((button) => {
      if (!button) return;
      if (!button.dataset.origHtml) button.dataset.origHtml = button.innerHTML;
      button.classList.add('is-added');
      button.textContent = Theme && Theme.translations && Theme.translations.added ? Theme.translations.added : 'Added ✓';
      setTimeout(() => {
        button.classList.remove('is-added');
        button.innerHTML = button.dataset.origHtml;
        this.buyTotal = this.querySelector('[ref="buyTotal"]');
        this.updateTotal();
      }, 1400);
    });
  }

  observeSticky() {
    if (!this.sticky || !this.actions || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', show);
          if (show) {
            this.sticky.removeAttribute('inert');
            this.sticky.setAttribute('aria-hidden', 'false');
          } else {
            this.sticky.setAttribute('inert', '');
            this.sticky.setAttribute('aria-hidden', 'true');
          }
        });
      },
      { threshold: 0 }
    );
    observer.observe(this.actions);
  }
}

if (!customElements.get('jane-buybox')) {
  customElements.define('jane-buybox', JaneBuybox);
}
